import { Platform } from 'react-native'
import QonversionService from 'app/utils/qonversion/QonversionService'
import { useSessionContext } from 'app/utils/supabase/useSessionContext'
import { useSupabase } from 'app/utils/supabase/useSupabase'
import { needsOnboarding } from 'app/utils/onboarding'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'solito/router'
import { useSubscriptionInfo } from 'app/utils/global-store/subscriptionStore'
// Extend the JwtPayload interface to include app_metadata
declare module 'jwt-decode' {
  interface JwtPayload {
    app_metadata?: {
      is_pending_deletion?: boolean;
      has_access?: boolean;
      is_payment_exempt?: boolean;
      [key: string]: any;
    };
  }
}

type Profile = {
  name: string | null
  core_memory: Record<string, any> | null
}

export const AuthStateChangeHandler = () => {
  const { session } = useSessionContext()
  const supabase = useSupabase()
  const router = useRouter()
  const { refreshSubscription } = useSubscriptionInfo()
  const hasCheckedProfile = useRef(false)
  const [isPendingDeletion, setIsPendingDeletion] = useState(false)
  const [hasNoAccess, setHasNoAccess] = useState(false)
  const [shouldRedirectToStart, setShouldRedirectToStart] = useState(false)
  const [isNavigationReady, setIsNavigationReady] = useState(false)

  // Set navigation ready after a short delay to ensure it's initialized
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsNavigationReady(true)
    }, 500)

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      if (event === 'SIGNED_OUT') {
        console.log('[AuthStateChangeHandler] Signed out, navigating to onboarding')
        // Only attempt to navigate if we're on web or navigation is ready
        if (Platform.OS !== 'web' && isNavigationReady) {
          router.replace('/onboarding')
          hasCheckedProfile.current = false
        } else {
          router.replace('/sign-in')
          hasCheckedProfile.current = false
        }
      }
      else if (event === 'TOKEN_REFRESHED') {
        console.log('[AuthStateChangeHandler] Token refreshed, refreshing subscription information')
        // Add 1-second delay before refreshing subscription
        setTimeout(async () => {
          await refreshSubscription()
          console.log('[AuthStateChangeHandler] Subscription information refreshed')
        }, 1000)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [supabase, router, isNavigationReady])

  // Effect to perform redirects when navigation is ready
  useEffect(() => {
    if (isNavigationReady) {
      if (isPendingDeletion) {
        router.replace('/pending-delete')
      } else if (hasNoAccess) {
        router.replace('/expired')
      } else if (shouldRedirectToStart) {
        router.replace('/start')
      }
    }
  }, [isNavigationReady, isPendingDeletion, hasNoAccess, shouldRedirectToStart, router])

  useEffect(() => {
    const checkProfile = async () => {
      // skip if no user or access token
      if (!session?.user?.id || !session?.access_token) return

      try {
        // Decode the JWT token
        const decoded = jwtDecode<JwtPayload>(session.access_token)

        // Check if user is pending deletion
        if (decoded?.app_metadata?.is_pending_deletion) {
          console.log('User is pending deletion, flagging for redirect')
          setIsPendingDeletion(true)
          hasCheckedProfile.current = false
          return
        }

        // Identify user in Qonversion when session changes
        try {
          const qonversionService = QonversionService.getInstance();
          await qonversionService.identifyUser(session.user.id, session.user.email);
        } catch (error) {
          console.error('[Qonversion] Error identifying user:', error);
        }

        // Check if user has access
        const hasAccess = decoded?.app_metadata?.has_access === true
        if (!hasAccess) {
          console.log('User does not have access, flagging for expired redirect')
          setHasNoAccess(true)
          hasCheckedProfile.current = false
          return
        }
      } catch (error) {
        console.error('Error decoding JWT token:', error)
      }

      // skip if we've already checked the current profile process
      if(hasCheckedProfile.current) return

      const { data, error } = (await supabase
        .from('profiles')
        .select('name, core_memory')
        .eq('id', session.user.id)
        .single()) as { data: Profile | null; error: any }

      let profile = data

      // Defensive: Parse core_memory if it's a string (Supabase serialization bug)
      if (profile?.core_memory && typeof profile.core_memory === 'string') {
        try {
          const parsedCoreMemory = JSON.parse(profile.core_memory)
          profile = { ...profile, core_memory: parsedCoreMemory }
        } catch (err) {
          console.error('[AuthStateChangeHandler] Failed to parse core_memory:', err)
          profile = { ...profile, core_memory: null }
        }
      }

      // Use centralized onboarding check
      if (!error && needsOnboarding(profile)) {
        console.log('User needs onboarding, flagging for redirect')
        setShouldRedirectToStart(true)
      }

      hasCheckedProfile.current = true
    }

    checkProfile()
  }, [session?.user?.id, session?.access_token])

  return null
}
